import { IonButton, IonContent, IonHeader, IonIcon, IonInput, IonItem, IonLabel, IonList, IonNote, IonPage, IonReorder, IonReorderGroup, IonSegment, IonSegmentButton, IonText, IonTitle, IonToggle, IonToolbar, useIonModal } from '@ionic/react';
import { IonButtons } from '@ionic/react';
import { type ReactElement, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useDeviceTransitPolicyMutation, useDeviceTransitPolicyQuery } from '../query/deviceTransitPolicyQuery';
import { JsonEditor, githubDarkTheme } from 'json-edit-react';
import { TransitPolicy, TransitPolicyRule } from '../models/TransitPolicy';
import { addOutline, list, codeSlash, copyOutline, chevronForward, createOutline, checkmarkOutline, lockClosedOutline, lockOpenOutline, notificationsOutline } from 'ionicons/icons';
import { EventClassification, EventTriggerSource } from '../models/DeviceEvent';
import { FlapState, MotionSensorState } from '../models/FrameMetadata';
import RfidChip from '../components/RfidChip';
import styles from './DoorPolicyModal.module.css';
import { useDeveloperModeQuery } from '../query/developerModeQuery';
import { Browser } from '@capacitor/browser';
import { CapacitorCrisp } from '@capgo/capacitor-crisp';
import { modalController } from '@ionic/core';
import type { ItemReorderEventDetail } from '@ionic/core';
import EditDoorPolicyRule from './EditDoorPolicyRule';
import { useCommonTranslation, useModalTranslation } from '../hooks/useTranslation';
import { useMediaQuery } from '../hooks/useMediaQuery';
import useUX from '../utils/ux';
import { formatDurationLabel, getClassificationLabel, getFlapStateLabel, getMotionSensorStateLabel, getTriggerSourceLabel } from '../utils/doorPolicyTranslations';
import { EditDoorPolicySoundPickerView, getSoundLabel } from './edit-door-policy/EditDoorPolicySoundPicker';
import { Trans } from 'react-i18next';

enum Tabs {
    Rules,
    Code
}

const isRecord = (value: unknown): value is Record<string, any> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const normalizeTransitPolicy = (policy: unknown): TransitPolicy | undefined => {
    if (!isRecord(policy)) {
        return undefined;
    }

    const normalizedPolicy = new TransitPolicy(policy);
    const rawRules = Array.isArray(policy.rules) ? policy.rules : [];
    normalizedPolicy.rules = rawRules.map((rule) =>
        rule instanceof TransitPolicyRule ? rule : new TransitPolicyRule(isRecord(rule) ? rule : {})
    );

    return normalizedPolicy;
};

const DoorPolicyModal = ({ dismiss, deviceTransitPolicyId }: { dismiss: (data?: string | null | undefined | number, role?: string) => void, deviceTransitPolicyId: number }) => {
    const [edited, setEdited] = useState(false);
    const query = useDeviceTransitPolicyQuery(deviceTransitPolicyId);
    const [name, setName] = useState('');
    const [transitPolicy, setTransitPolicy] = useState<TransitPolicy>();
    const [tab, setTab] = useState<Tabs>(Tabs.Rules);
    const [isReorderMode, setIsReorderMode] = useState(false);
    const mutation = useDeviceTransitPolicyMutation(deviceTransitPolicyId);
    const developerModeQuery = useDeveloperModeQuery();
    const developerMode = !!developerModeQuery.data;
    const [ruleModalState, setRuleModalState] = useState<{ mode: 'edit' | 'add'; index: number } | null>(null);
    const { t: tCommon } = useCommonTranslation();
    const { t: tModals } = useModalTranslation();
    const policyRules = Array.isArray(transitPolicy?.rules) ? transitPolicy.rules : [];
    const selectedRule =
        ruleModalState?.mode === 'edit' ? policyRules[ruleModalState.index] : undefined;
    const ruleForModal =
        ruleModalState?.mode === 'add' ? () => new TransitPolicyRule({}) : selectedRule;
    const [presentEditRuleModal, dismissEditRuleModal] = useIonModal(EditDoorPolicyRule, {
        dismiss: () => {
            dismissEditRuleModal();
            setRuleModalState(null);
        },
        onDelete: (index?: number) => {
            if (index === undefined) {
                return;
            }
            setTransitPolicy((prevPolicy) => {
                if (!prevPolicy) {
                    return prevPolicy;
                }
                const rules = Array.isArray(prevPolicy.rules) ? [...prevPolicy.rules] : [];
                rules.splice(index, 1);
                return new TransitPolicy({
                    ...prevPolicy,
                    rules,
                });
            });
            setEdited(true);
        },
        deviceId: query.data?.deviceId ?? '',
        onSave: (updatedRule: TransitPolicyRule, index?: number) => {
            if (index === undefined) {
                return;
            }
            setTransitPolicy((prevPolicy) => {
                if (!prevPolicy) {
                    return prevPolicy;
                }
                const rules = Array.isArray(prevPolicy.rules) ? [...prevPolicy.rules] : [];
                if (ruleModalState?.mode === 'add') {
                    rules.splice(index, 0, updatedRule);
                } else {
                    rules[index] = updatedRule;
                }
                return new TransitPolicy({
                    ...prevPolicy,
                    rules,
                });
            });
            setEdited(true);
        },
        rule: ruleForModal,
        ruleIndex: ruleModalState?.index,
    });
    const activationSound = transitPolicy?.ux?.onActivate?.sound ?? '';
    const activationSoundLabel = getSoundLabel(tModals, activationSound);
    const [presentSoundPickerModal, dismissSoundPickerModal] = useIonModal(EditDoorPolicySoundPickerView, {
        soundInput: activationSound,
        onSelectSound: (soundId: string) => {
            setTransitPolicy((prevPolicy) => {
                if (!prevPolicy) {
                    return prevPolicy;
                }
                return new TransitPolicy({
                    ...prevPolicy,
                    ux: {
                        ...(prevPolicy.ux ?? {}),
                        onActivate: {
                            ...(prevPolicy.ux?.onActivate ?? {}),
                            sound: soundId,
                        },
                    },
                });
            });
            setEdited(true);
        },
        onClose: () => {
            dismissSoundPickerModal();
        },
    });

    useEffect(() => {
        setName(query.data?.name || '');
        setTransitPolicy(normalizeTransitPolicy(query.data?.transitPolicy));
        setEdited(false);
    }, [query.data?.transitPolicy]);

    useEffect(() => {
        if (!ruleModalState) {
            return;
        }
        if (!query.data?.deviceId) {
            return;
        }

        const showModal = async () => {
            const presentingElement = await modalController.getTop();
            await presentEditRuleModal({
                presentingElement: presentingElement ?? undefined,
            });
        };

        showModal();
    }, [presentEditRuleModal, query.data?.deviceId, ruleModalState]);

    const save = async () => {
        await mutation.mutateAsync({ transitPolicy, name });

        dismiss(undefined, 'confirm');
    }

    // Stable row keys per rule instance: reordering reuses the same
    // TransitPolicyRule objects, so React moves the DOM nodes instead of
    // rewriting their content (index/description keys flashed on drop).
    const ruleKeysRef = useRef({ map: new WeakMap<TransitPolicyRule, number>(), next: 1 });
    const ruleKey = (rule: TransitPolicyRule) => {
        const store = ruleKeysRef.current;
        let key = store.map.get(rule);
        if (key === undefined) {
            key = store.next++;
            store.map.set(rule, key);
        }
        return key;
    };

    const handleReorder = (event: CustomEvent<ItemReorderEventDetail>) => {
        if (!transitPolicy) {
            event.detail.complete();
            return;
        }

        // complete() finalizes the order with no settle animation: the
        // dragged card teleports from under the finger into its slot, and
        // displaced cards still mid-transition teleport the rest of the way.
        // FLIP every row: capture the visual positions at release (rects
        // include in-flight transforms), then animate each row from there
        // into its settled slot.
        const rows = Array.from((event.target as HTMLElement).children) as HTMLElement[];
        const releaseTops = new Map(rows.map((row) => [row, row.getBoundingClientRect().top]));

        const nextRules = event.detail.complete([...policyRules]);
        // Commit the reordered rows synchronously: React 19 otherwise
        // schedules the commit on its own task, which can land after the
        // settle measurement below and shift rows mid-animation.
        flushSync(() => {
            setTransitPolicy(new TransitPolicy({
                ...transitPolicy,
                rules: nextRules,
            }));
            setEdited(true);
        });

        // complete() clears transforms inside its own requestAnimationFrame;
        // ours is queued after it, so the settled positions are measurable
        // here. Inline styles (not element.animate, whose pending start
        // misses this frame's paint) pin each row back to its release
        // position for this paint, then the next frame transitions it home.
        requestAnimationFrame(() => {
            const moved: HTMLElement[] = [];
            for (const row of rows) {
                const releaseTop = releaseTops.get(row);
                if (releaseTop === undefined) {
                    continue;
                }
                const deltaY = releaseTop - row.getBoundingClientRect().top;
                if (Math.abs(deltaY) < 1) {
                    continue;
                }
                row.style.transition = 'none';
                row.style.transform = `translateY(${deltaY}px)`;
                moved.push(row);
            }
            if (moved.length === 0) {
                return;
            }
            requestAnimationFrame(() => {
                for (const row of moved) {
                    row.style.transition = 'transform 250ms cubic-bezier(0.2, 0, 0, 1)';
                    row.style.transform = '';
                }
                setTimeout(() => {
                    for (const row of moved) {
                        row.style.transition = '';
                    }
                }, 300);
            });
        });
    };

    const openEditRuleModal = (ruleIndex: number) => {
        setRuleModalState({ mode: 'edit', index: ruleIndex });
    };

    const openAddRuleModal = (insertIndex: number) => {
        setRuleModalState({ mode: 'add', index: insertIndex });
    };

    const openCrispChat = async () => {
        await CapacitorCrisp.openMessenger();
    };

    const { presentResult } = useUX();
    const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');

    const copyPolicyCode = () => {
        if (!transitPolicy || !navigator.clipboard?.writeText) {
            return;
        }
        presentResult(
            () => navigator.clipboard.writeText(JSON.stringify(transitPolicy, null, 2)),
            tModals('door_policy.code_editor.copied')
        );
    };

    const idleLockEnabled = transitPolicy?.idleLock ?? true;
    const idleLockBatteryEnabled = transitPolicy?.idleLockBattery ?? idleLockEnabled;
    const unlockOnPowerLoss = !idleLockBatteryEnabled;

    const setIdleLock = (idleLock: boolean) => {
        setTransitPolicy((prevPolicy) => {
            if (!prevPolicy) {
                return prevPolicy;
            }
            return new TransitPolicy({
                ...prevPolicy,
                idleLock,
            });
        });
        setEdited(true);
    };

    const setIdleLockBattery = (unlockOnPowerLoss: boolean) => {
        setTransitPolicy((prevPolicy) => {
            if (!prevPolicy) {
                return prevPolicy;
            }
            return new TransitPolicy({
                ...prevPolicy,
                idleLockBattery: !unlockOnPowerLoss,
            });
        });
        setEdited(true);
    };

    const openActivationSoundPicker = async () => {
        const presentingElement = await modalController.getTop();
        await presentSoundPickerModal({
            presentingElement: presentingElement ?? undefined,
        });
    };

    const renderActionChips = (rule: TransitPolicyRule) => {
        const chips: ReactElement[] = [];
        const action = rule?.action ?? {};
        const hasLockAction =
            action.lock !== undefined ||
            action.lockoutDuration !== undefined ||
            action.final !== undefined;

        if (hasLockAction) {
            if (action.lock === false) {
                chips.push(
                    <span key="lock" className={`${styles.actionChip} ${styles.actionChipUnlock}`}>
                        <IonIcon icon={lockOpenOutline} aria-hidden="true" />
                        {tModals('door_policy.rule_summary.unlock')}
                    </span>
                );
            } else {
                let label = tModals('door_policy.rule_summary.lock');
                if (action.lockoutDuration !== undefined && Number(action.lockoutDuration) > 0) {
                    const durationLabel = formatDurationLabel(tModals, Number(action.lockoutDuration), { short: true });
                    label += ` ${tModals('door_policy.rule_summary.for_duration', { duration: durationLabel })}`;
                } else if (action.final) {
                    label += ` ${tModals('door_policy.rule_summary.until_event_end')}`;
                }
                chips.push(
                    <span key="lock" className={`${styles.actionChip} ${styles.actionChipLock}`}>
                        <IonIcon icon={lockClosedOutline} aria-hidden="true" />
                        {label}
                    </span>
                );
            }
        }

        if (action.sound !== undefined) {
            chips.push(
                <span key="sound" className={`${styles.actionChip} ${styles.actionChipSound}`}>
                    <IonIcon icon={notificationsOutline} aria-hidden="true" />
                    {getSoundLabel(tModals, String(action.sound))}
                </span>
            );
        }

        Object.keys(action)
            .filter((key) => !['lock', 'lockoutDuration', 'final', 'sound'].includes(key))
            .forEach((key) => {
                chips.push(
                    <span key={key} className={styles.actionChip}>
                        {tModals('door_policy.rule_summary.unknown_action')}
                    </span>
                );
            });

        if (chips.length === 0) {
            chips.push(
                <span key="none" className={styles.actionChip}>
                    {tModals('door_policy.rule_summary.do_nothing')}
                </span>
            );
        }

        return chips;
    };

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonButton color="medium" onClick={() => dismiss(null, 'cancel')}>
                            {tCommon('buttons.cancel')}
                        </IonButton>
                    </IonButtons>
                    {/* ios title mode in both platforms: the md title is a flex
                        item offset by the differing Cancel/Save widths, while
                        the ios title is absolutely centered on the toolbar.
                        (mode is honored at runtime but missing from IonTitle's
                        React typings, hence the cast.) */}
                    <IonTitle {...({ mode: 'ios' } as object)}>
                        <IonInput
                            aria-label={tModals('door_policy.name_label')}
                            mode="ios"
                            className={styles.nameInput}
                            value={name}
                            placeholder={tModals('door_policy.name_placeholder')}
                            onIonInput={(e) => {
                                if (transitPolicy) {
                                    setName(e.detail.value!);
                                    setEdited(true);
                                }
                            }}
                        ></IonInput>
                    </IonTitle>
                    <IonButtons slot="end">
                        <IonButton onClick={save} strong={true} disabled={!edited || mutation.isPending}>
                            {tCommon('buttons.save')}
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
                {developerMode && (
                    <IonToolbar>
                        <IonSegment value={tab} onIonChange={e => setTab(e.detail.value as Tabs)}>
                            <IonSegmentButton layout="icon-start" value={Tabs.Rules}>
                                <IonIcon icon={list} />
                                <IonLabel>{tModals('door_policy.tabs.rules')}</IonLabel>
                            </IonSegmentButton>
                            <IonSegmentButton layout="icon-start" value={Tabs.Code}>
                                <IonIcon icon={codeSlash} />
                                <IonLabel>{tModals('door_policy.tabs.code')}</IonLabel>
                            </IonSegmentButton>
                        </IonSegment>
                    </IonToolbar>
                )}
            </IonHeader>
            <IonContent color="light" fullscreen className={styles.content}>
                {tab === Tabs.Rules && (
                    <div className={styles.wrapper}>
                        <section className={styles.section}>
                            <div className={styles.sectionHeaderRow}>
                                <h2 className={styles.sectionLabel}>{tModals('door_policy.sections.rules')}</h2>
                                <IonButton
                                    fill="clear"
                                    size="small"
                                    className={styles.sectionAction}
                                    onClick={() => setIsReorderMode((prev) => !prev)}
                                >
                                    <IonIcon
                                        slot="start"
                                        icon={isReorderMode ? checkmarkOutline : createOutline}
                                    />
                                    {isReorderMode
                                        ? tModals('door_policy.buttons.done')
                                        : tModals('door_policy.buttons.reorder')}
                                </IonButton>
                            </div>
                            <IonReorderGroup
                                disabled={!isReorderMode}
                                onIonItemReorder={handleReorder}
                                className={styles.ruleList}
                            >
                                {policyRules.map((rule, idx) => (
                                    <IonItem
                                        key={ruleKey(rule)}
                                        lines="none"
                                        detail={false}
                                        className={styles.ruleItem}
                                        onClick={() => {
                                            openEditRuleModal(idx);
                                        }}
                                    >
                                        <div className={styles.ruleItemInner}>
                                            {idx > 0 && !isReorderMode && (
                                                <div className={styles.ruleConnector}>
                                                    {tModals('door_policy.rule_summary.if_not_check')}
                                                </div>
                                            )}
                                            <div className={`${styles.ruleCardBase} ${styles.ruleCardButton}`}>
                                                <div className={styles.ruleText}>
                                                    {!rule?.criteria && <span>{tModals('door_policy.rule_summary.always')}</span>}
                                                    {rule?.criteria && Object.entries(rule?.criteria).map(([key, value], criteriaIdx) => {
                                                        const parts: ReactElement[] = [];
                                                        let partIndex = 0;
                                                        const pushText = (text: string) => {
                                                            parts.push(
                                                                <span key={`${key}-${criteriaIdx}-text-${partIndex++}`}>{text}</span>
                                                            );
                                                        };
                                                        const pushStrong = (text: string) => {
                                                            parts.push(
                                                                <strong key={`${key}-${criteriaIdx}-strong-${partIndex++}`}>{text}</strong>
                                                            );
                                                        };
                                                        const pushNode = (node: ReactElement) => {
                                                            parts.push(
                                                                <span key={`${key}-${criteriaIdx}-node-${partIndex++}`}>{node}</span>
                                                            );
                                                        };

                                                        const orLabel = tModals('door_policy.rule_summary.or');
                                                        pushText(
                                                            `${criteriaIdx === 0 ? tModals('door_policy.rule_summary.if') : tModals('door_policy.rule_summary.and')} `
                                                        );
                                                        value = Array.isArray(value) ? value : [value];

                                                        switch (key) {
                                                            case 'timeRange':
                                                                for (let i = 0; i < value.length; i++) {
                                                                    pushText(
                                                                        !i
                                                                            ? ` ${tModals('door_policy.rule_summary.time_between')} `
                                                                            : ` ${orLabel} `
                                                                    );
                                                                    pushStrong(String(value[i]));
                                                                }
                                                                break;

                                                            case 'eventTriggerSource':
                                                                for (let i = 0; i < value.length; i++) {
                                                                    pushText(
                                                                        !i
                                                                            ? ` ${tModals('door_policy.rule_summary.event_triggered_by')} `
                                                                            : ` ${orLabel} `
                                                                    );
                                                                    pushStrong(getTriggerSourceLabel(tModals, Number(value[i]) as EventTriggerSource));
                                                                }
                                                                break;

                                                            case 'eventClassification':
                                                                for (let i = 0; i < value.length; i++) {
                                                                    pushText(
                                                                        !i
                                                                            ? ` ${tModals('door_policy.rule_summary.appears_to_be')} `
                                                                            : ` ${orLabel} `
                                                                    );
                                                                    pushStrong(getClassificationLabel(tModals, Number(value[i]) as EventClassification));
                                                                }
                                                                break;

                                                            case 'motionSensorState':
                                                                for (let i = 0; i < value.length; i++) {
                                                                    pushText(
                                                                        !i
                                                                            ? ` ${tModals('door_policy.rule_summary.motion_sensor_is')} `
                                                                            : ` ${orLabel} `
                                                                    );
                                                                    pushStrong(getMotionSensorStateLabel(tModals, Number(value[i]) as MotionSensorState));
                                                                }
                                                                break;

                                                            case 'flapState':
                                                                for (let i = 0; i < value.length; i++) {
                                                                    pushText(
                                                                        !i
                                                                            ? ` ${tModals('door_policy.rule_summary.flap_is')} `
                                                                            : ` ${orLabel} `
                                                                    );
                                                                    pushStrong(getFlapStateLabel(tModals, Number(value[i]) as FlapState));
                                                                }
                                                                break;

                                                            case 'rfidCode':
                                                                for (let i = 0; i < value.length; i++) {
                                                                    pushText(
                                                                        !i
                                                                            ? ` ${tModals('door_policy.rule_summary.read_microchip')} `
                                                                            : ` ${orLabel} `
                                                                    );
                                                                    pushNode(<RfidChip compact deviceId={query.data?.deviceId} rfidCode={value[i]} />);
                                                                }
                                                                break;

                                                            case 'rfidTimeout': {
                                                                pushText(` ${tModals('door_policy.rule_summary.waiting_up_to')} `);
                                                                pushStrong(formatDurationLabel(tModals, Number(value[0]), { short: true }));
                                                                const timeoutSuffix = tModals('door_policy.rule_summary.for_a_microchip');
                                                                if (timeoutSuffix) {
                                                                    pushText(` ${timeoutSuffix}`);
                                                                }
                                                                break;
                                                            }

                                                            default:
                                                                pushText(tModals('door_policy.rule_summary.unknown_criteria'));
                                                        }

                                                        return <span key={`${key}-${criteriaIdx}`}>{parts}</span>;
                                                    })}
                                                </div>
                                                <div className={styles.ruleChips}>{renderActionChips(rule)}</div>
                                            </div>
                                        </div>
                                        <IonReorder slot="end" className={styles.reorderHandle} />
                                    </IonItem>
                                ))}
                            </IonReorderGroup>
                            {!isReorderMode && (
                                <p className={styles.noMatchText}>{tModals('door_policy.rule_summary.no_match')}</p>
                            )}
                            <IonButton
                                expand="block"
                                fill="outline"
                                className={styles.addRuleButton}
                                onClick={() => {
                                    openAddRuleModal(policyRules.length);
                                }}
                            >
                                <IonIcon slot="start" icon={addOutline} />
                                {tModals('door_policy.buttons.add_rule')}
                            </IonButton>
                        </section>
                        <section className={styles.section}>
                            <h2 className={styles.sectionLabel}>{tModals('door_policy.sections.behavior')}</h2>
                            <IonList className={styles.listCard}>
                                <IonItem>
                                    <IonToggle
                                        checked={!idleLockEnabled}
                                        helperText={tModals('door_policy.behavior.training_mode_helper')}
                                        disabled={!transitPolicy}
                                        onIonChange={(e) => setIdleLock(!e.detail.checked)}
                                    >
                                        <IonLabel>{tModals('door_policy.behavior.training_mode_label')}</IonLabel>
                                    </IonToggle>
                                </IonItem>
                                <IonItem>
                                    <IonToggle
                                        checked={unlockOnPowerLoss}
                                        disabled={!transitPolicy}
                                        onIonChange={(e) => setIdleLockBattery(e.detail.checked)}
                                    >
                                        <IonLabel>{tModals('door_policy.behavior.power_loss_label')}</IonLabel>
                                    </IonToggle>
                                </IonItem>
                                <IonItem
                                    button
                                    onClick={openActivationSoundPicker}
                                    disabled={!transitPolicy}
                                >
                                    <IonLabel>{tModals('door_policy.behavior.activation_sound_label')}</IonLabel>
                                    <IonText slot="end" color="medium">{activationSoundLabel}</IonText>
                                </IonItem>
                            </IonList>
                        </section>
                        <p className={styles.helperText}>
                            <Trans
                                i18nKey="door_policy.support"
                                ns="modals"
                                components={{
                                    supportLink: (
                                        <button type="button" className={styles.supportLink} onClick={openCrispChat} />
                                    ),
                                }}
                            />
                        </p>
                    </div>
                )}
                {tab === Tabs.Code && (
                    <div className={styles.wrapper}>
                        <div className={styles.introCard}>
                            <span className={styles.introIcon} aria-hidden="true">
                                <IonIcon icon={codeSlash} />
                            </span>
                            <div className={styles.introBody}>
                                <h2 className={styles.introTitle}>{tModals('door_policy.code_editor.title')}</h2>
                                <p className={styles.introText}>{tModals('door_policy.code_editor.description')}</p>
                                <button
                                    type="button"
                                    className={styles.introLink}
                                    onClick={() => Browser.open({ url: "https://www.onlycat.com/door-policy-schema/" })}
                                >
                                    {tModals('door_policy.code_editor.view_schema')}
                                    <IonIcon icon={chevronForward} aria-hidden="true" />
                                </button>
                            </div>
                        </div>
                        <section className={styles.section}>
                            <div className={styles.sectionHeaderRow}>
                                <h2 className={styles.sectionLabel}>{tModals('door_policy.sections.code')}</h2>
                                <IonButton
                                    fill="clear"
                                    size="small"
                                    className={styles.sectionAction}
                                    disabled={!transitPolicy}
                                    onClick={copyPolicyCode}
                                >
                                    <IonIcon slot="start" icon={copyOutline} />
                                    {tModals('door_policy.buttons.copy')}
                                </IonButton>
                            </div>
                            {transitPolicy && (
                                <div className={`${styles.ruleCardBase} ${styles.codeCard}`}>
                                    <JsonEditor data={transitPolicy}
                                        setData={(data) => {
                                            const nextPolicy = normalizeTransitPolicy(data);
                                            if (!nextPolicy) {
                                                return;
                                            }

                                            setTransitPolicy(nextPolicy);
                                            setEdited(true);
                                        }}
                                        theme={[
                                            ...(prefersDark ? [githubDarkTheme] : []),
                                            { container: { backgroundColor: 'transparent' } },
                                        ]}
                                        showArrayIndices={false}
                                        showCollectionCount="when-closed"
                                        maxWidth="100%"
                                        rootName="transitPolicy"
                                        keySort={(a, b) => {
                                            const aStr = String(a);
                                            const bStr = String(b);

                                            if (aStr === 'description') return -1;
                                            if (bStr === 'description') return 1;
                                            if (aStr === 'criteria') return -1;
                                            if (bStr === 'criteria') return 1;
                                            return aStr.localeCompare(bStr);
                                        }}
                                    />
                                </div>
                            )}
                        </section>
                        <p className={styles.helperText}>
                            <Trans
                                i18nKey="door_policy.support"
                                ns="modals"
                                components={{
                                    supportLink: (
                                        <button type="button" className={styles.supportLink} onClick={openCrispChat} />
                                    ),
                                }}
                            />
                        </p>
                    </div>
                )}
            </IonContent>
        </IonPage >
    );
};

export default DoorPolicyModal;
